<script setup>
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import MessageApi from 'dashboard/api/inbox/message';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const emit = defineEmits(['openMessages']);

const SEARCH_DEBOUNCE_MS = 400;
const HIGHLIGHT_CLASS = 'font-semibold bg-n-amber-3 rounded-sm';
const SNIPPET_CONTEXT_CHARS = 40;

const store = useStore();
const { t } = useI18n();
const { run, abort, isPending } = useAbortableRequest();
const { getPlainText, highlightContent } = useMessageFormatter();

const searchInput = useTemplateRef('searchInput');
const isOpen = ref(false);
const query = ref('');
const results = ref([]);
const hasSearched = ref(false);

const currentChat = computed(() => store.getters.getSelectedChat);

const reset = () => {
  abort();
  query.value = '';
  results.value = [];
  hasSearched.value = false;
};

const close = () => {
  isOpen.value = false;
  reset();
};

watch(() => currentChat.value.id, close);

const toggle = async () => {
  if (isOpen.value) {
    close();
    return;
  }
  isOpen.value = true;
  await nextTick();
  searchInput.value.focus();
};

const search = debounce(async term => {
  // Drop searches queued before the dropdown closed or the query changed
  if (!isOpen.value || term !== query.value.trim()) return;
  if (!term) {
    reset();
    return;
  }
  try {
    const payload = await run(
      async signal =>
        (
          await MessageApi.search(currentChat.value.id, term, {
            signal,
          })
        ).data.payload
    );
    if (payload === undefined) return;
    results.value = payload;
    hasSearched.value = true;
  } catch {
    useAlert(t('CONVERSATION.HEADER.SEARCH.ERROR'));
  }
}, SEARCH_DEBOUNCE_MS);

// Drop the in-flight request and the shown results right away so they never show under the new query
const onInput = event => {
  abort();
  results.value = [];
  hasSearched.value = false;
  search(event.target.value.trim());
};

// Start long messages near the first match so the clamped preview shows it
const resultSnippet = content => {
  const term = query.value.trim();
  const text = getPlainText(content || '');
  const matchIndex = text.toLowerCase().indexOf(term.toLowerCase());
  const snippet =
    matchIndex > SNIPPET_CONTEXT_CHARS
      ? `…${text.slice(matchIndex - SNIPPET_CONTEXT_CHARS)}`
      : text;
  return highlightContent(snippet, term, HIGHLIGHT_CLASS);
};

const isMessageLoaded = messageId =>
  currentChat.value.messages.some(message => message.id === messageId);

const jumpToMessage = async messageId => {
  const conversationId = currentChat.value.id;
  close();
  // The messages may be hidden behind a dashboard app tab
  emit('openMessages');
  if (!isMessageLoaded(messageId)) {
    try {
      // One request loads the match and the history up to the nearest newer loaded message,
      // an earlier jump far back can leave a gap in the loaded history
      await store.dispatch('fetchPreviousMessages', {
        conversationId,
        after: messageId,
        before: currentChat.value.messages.find(
          message => message.id > messageId
        ).id,
      });
    } catch {
      useAlert(t('CONVERSATION.HEADER.SEARCH.MESSAGE_NOT_FOUND'));
      return;
    }
  }
  if (currentChat.value.id !== conversationId) return;
  // Scroll directly: the SCROLL_TO_MESSAGE handler starts a smooth scroll and then
  // restores the previous scroll position while it fetches older messages
  await nextTick();
  // WhatsApp chats hide duplicate messages, so a loaded match may not be rendered
  const element = document.getElementById(`message${messageId}`);
  if (!element) {
    useAlert(t('CONVERSATION.HEADER.SEARCH.MESSAGE_NOT_FOUND'));
    return;
  }
  element.scrollIntoView({ block: 'center' });
};
</script>

<template>
  <div v-on-clickaway="close" class="xl:relative flex items-center">
    <Button
      v-tooltip="$t('CONVERSATION.HEADER.SEARCH.BUTTON')"
      faded
      slate
      sm
      icon="i-lucide-search"
      :class="{ '!bg-n-alpha-2 !text-n-blue-11': isOpen }"
      @click="toggle"
    />
    <div
      v-if="isOpen"
      class="absolute top-full mt-1 start-0 xl:start-auto xl:end-0 z-30 flex flex-col w-96 max-w-full xl:max-w-[calc(100vw-2rem)] max-h-[28rem] rounded-xl border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg"
    >
      <div
        class="flex items-center gap-2 px-3 py-2 border-b border-n-weak shrink-0"
      >
        <span class="i-lucide-search size-4 text-n-slate-11 shrink-0" />
        <input
          ref="searchInput"
          v-model="query"
          type="text"
          class="flex-1 min-w-0 !mb-0 !p-0 !border-0 !bg-transparent !outline-none text-sm text-n-slate-12 placeholder:text-n-slate-10"
          :placeholder="$t('CONVERSATION.HEADER.SEARCH.PLACEHOLDER')"
          @input="onInput"
          @keydown.esc.stop="close"
        />
        <Spinner v-if="isPending" class="size-4 text-n-slate-11" />
      </div>
      <ul v-if="results.length" class="overflow-y-auto p-1">
        <li v-for="message in results" :key="message.id">
          <button
            type="button"
            class="flex flex-col gap-1 w-full px-3 py-2 text-start rounded-lg hover:bg-n-alpha-2"
            @click="jumpToMessage(message.id)"
          >
            <span class="flex items-center justify-between gap-2 w-full">
              <span class="text-xs font-medium truncate text-n-slate-11">
                {{ message.sender_name }}
              </span>
              <span class="text-xs shrink-0 text-n-slate-10">
                {{ messageTimestamp(message.created_at, 'MMM d, yyyy h:mm a') }}
              </span>
            </span>
            <span
              v-dompurify-html="resultSnippet(message.content)"
              class="text-sm text-n-slate-12 line-clamp-2 break-words"
            />
          </button>
        </li>
      </ul>
      <p
        v-else-if="hasSearched && !isPending"
        class="px-3 py-4 text-sm text-center text-n-slate-11"
      >
        {{ $t('CONVERSATION.HEADER.SEARCH.NO_RESULTS') }}
      </p>
    </div>
  </div>
</template>
